import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
import { MAX_IMAGE_BYTES, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
  /** Udfyldt hvis filen ikke kan bruges — blokerer indsendelse indtil den fjernes. */
  problem: string | null;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  /** Id på en påbegyndt, ufærdig henvendelse — genbruges ved nyt forsøg. */
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const invalid = selected.filter(item => item.problem);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSubmitError('');
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-98" data-mp="31uln9hfho5rt">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-99" data-mp="31wetivm6wnza">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-100" data-mp="2x22d6ug0fsrr">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-101" data-mp="3brgnc4reue87">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-102" data-mp="36tf0d8qv5urg">
              Fortæl om din opgave
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-103" data-mp="3kr3n6m4hrbz5">
              Beskriv kort hvad du skal have lavet. Du må meget gerne vedhæfte billeder, så er det nemmere at
              vurdere opgaven.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-104" data-mp="1lglpac07eq77">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-105" data-mp="z2v3bdsvzmt">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-106" data-mp="3oac5nv8pp709" />
                  {settings.phone}
                </LinkButton>}
              <LinkButton href={`mailto:${CONTACT_EMAIL}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-107" data-mp="3ecxti7ttn4dr">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-108" data-mp="35cj52zlk9961" />
                {CONTACT_EMAIL}
              </LinkButton>
            </div>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-109" data-mp="39fo7711qb8u5">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-110" data-mp="dx0o5n0ny30y">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-111" data-mp="8wmqmywoubh6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-112" data-mp="3zcpwn3umde1">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-113" data-mp="3rbnijve717ip">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-114" data-mp="3mbranl2dkvxv">
                Send en opgave mere
              </Button>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          if (invalid.length > 0) {
            setSubmitError(`Billedet “${invalid[0].file.name}” kunne ikke bruges. Vælg et andet billede eller fjern det.`);
            return;
          }
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file),
            draftId
          });
          setSubmitting(false);
          if (!result.ok) {
            // Samme dokument genbruges ved næste forsøg, så der ikke opstår dubletter.
            setDraftId(result.draftId);
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            return;
          }
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setDraftId(undefined);
          setSent(true);
        }} data-id="element-115" data-mp="3cdbl3mizxuqh">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-116" data-mp="n6osc6ej0uoy">
                Felter markeret med <span className="text-accent" data-id="element-117" data-mp="3vkf9vbpq92p5">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-118" data-mp="3rk3qe293p8f9">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-119" data-mp="3rlolbszbutdn" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-120" data-mp="1ft9hd07j4upc" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="dig@mail.dk" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-121" data-mp="3hfna57rplpax" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Hvidovre" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-122" data-mp="3cmltau7xzfop" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-123" data-mp="20bhzjmuv07gl" />

              <div className="mt-5" data-id="element-124" data-mp="2j3kg6y7i3nqu">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-125" data-mp="2e3endephvv76">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-126" data-mp="2a235jvcv4e9j">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-127" data-mp="3aaj96bdsige1" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-128" data-mp="3ebczfbepg7a3">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-129" data-mp="3jb7rvje4p4x9">
                    Valgfrit — billeder op til {Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const picked = Array.from(input.files ?? []).map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  problem: validateImage(file)
                }));
                setSelected(current => [...current, ...picked]);
                setSubmitError('');
                input.value = '';
              }} data-id="element-130" data-mp="3o3is6ncs6sqc" />
                </label>

                {invalid.length > 0 && <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-131" data-mp="39bnffv4vd3z0">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-132" data-mp="3ie54ehbwsapp" />
                    <span data-id="element-133" data-mp="3nby5ivycuej4">
                      {invalid.map(item => <span key={item.file.name} className="block" data-id="element-134" data-mp="3s36gvxj8w9yx">
                          Billedet “{item.file.name}” kunne ikke bruges. Vælg et andet billede eller fjern det.
                        </span>)}
                    </span>
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-135" data-mp="9ycztuurjv1k">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className={`overflow-hidden rounded-xl border ${item.problem ? 'border-danger' : 'border-line'}`} data-id="element-136" data-mp="1udhti73pwh70" data-mpi={index}>
                        <div className="relative" data-id="element-137" data-mp="1ghnv4i0kla85">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-138" data-mp="2e439vc1m3ts0" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-139" data-mp="24ch3bt07yivl">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-140" data-mp="u75ygmbmw294" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-141" data-mp="gf7z337q5kq5">
                          <span className={`truncate text-xs ${item.problem ? 'text-danger' : 'text-ink-muted'}`} data-id="element-142" data-mp="le2h42xvb597">
                            {item.file.name}
                          </span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-143" data-mp="bejypqxlvrj9">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-144" data-mp="21zmp63s4btwr">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-145" data-mp="2b01dlc0dpp4h" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-146" data-mp="2kqdv0vcwd26e">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-147" data-mp="2uo1ufylg6xuw" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || invalid.length > 0} data-id="element-148" data-mp="14bmbz91n579r">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-149" data-mp="16oyaqa0gazsr" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send forespørgsel'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}